import { Search } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { CompAnalysisJob } from '@/services/competitor-analysis/types'
import StageCard from './components/StageCard'

interface CompetitorAnalysisPipelineProps {
  job: CompAnalysisJob
  onViewStage?: (stageName: string) => void
}

export default function CompetitorAnalysisPipeline({ job, onViewStage }: CompetitorAnalysisPipelineProps) {
  const domain = (() => {
    try { return new URL(job.brand_url).hostname.replace('www.', '') }
    catch { return job.brand_url }
  })()

  const completedCount = job.stages.filter(
    s => s.status === 'completed' || s.status === 'degraded' || s.status === 'skipped'
  ).length

  return (
    <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Search className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-tf-text">
              Analyzing {job.brand_name || domain}
            </h2>
            <p className="text-xs text-tf-text-muted">
              Stage {completedCount} of {job.stages.length}
            </p>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-tf-text-muted">Progress</span>
          <span className="text-xs font-bold text-purple-500">{job.progress_pct}%</span>
        </div>
        <div className="h-2 bg-tf-bg-muted rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-700 ease-out',
              job.status === 'failed' ? 'bg-red-500' :
              job.status === 'completed' ? 'bg-emerald-500' :
              'bg-purple-500',
            )}
            style={{ width: `${job.progress_pct}%` }}
          />
        </div>
      </div>

      {/* Stage list */}
      <div className="space-y-2 flex-1 overflow-auto">
        {job.stages.map((stage, idx) => (
          <StageCard
            key={stage.name}
            stage={stage}
            index={idx}
            isActive={stage.name === job.current_stage}
            onViewDetail={onViewStage ? () => onViewStage(stage.name) : undefined}
          />
        ))}
      </div>

      {/* Live insight */}
      {job.status === 'running' && (
        <div className="mt-4 px-4 py-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
          <p className="text-xs text-purple-400">
            {getCurrentInsight(job)}
          </p>
        </div>
      )}

      {/* Error message */}
      {job.status === 'failed' && job.error_message && (
        <div className="mt-4 px-4 py-3 rounded-lg bg-red-500/5 border border-red-500/20">
          <p className="text-xs text-red-400">{job.error_message}</p>
        </div>
      )}
    </div>
  )
}

function getCurrentInsight(job: CompAnalysisJob): string {
  // Find the most recent completed stage with an insight
  const completedStages = [...job.stages]
    .filter(s => s.insight && (s.status === 'completed' || s.status === 'degraded'))
    .reverse()

  if (completedStages.length > 0) {
    return completedStages[0].insight!
  }

  // Find running stage
  const running = job.stages.find(s => s.status === 'running')
  if (running) {
    return `Processing ${running.display_name.toLowerCase()}...`
  }

  return 'Processing...'
}
